import openai from './openaiClient';
import { 
  APIConnectionError,
  AuthenticationError,
  PermissionDeniedError,
  RateLimitError,
  InternalServerError
} from 'openai';

/**
 * Maps OpenAI API error types to user-friendly error messages.
 * @param {Error} error - The error object from OpenAI API.
 * @returns {Object} Error info with isInternal flag and message.
 */
function getErrorMessage(error) {
  if (error instanceof AuthenticationError) {
    return { isInternal: true, message: 'Invalid API key or authentication failed. Please check your OpenAI API key.' };
  } else if (error instanceof PermissionDeniedError) {
    return { isInternal: true, message: 'Quota exceeded or authorization failed. You may have exceeded your usage limits or do not have access to this resource.' };
  } else if (error instanceof RateLimitError) {
    return { isInternal: true, message: 'Rate limit exceeded. You are sending requests too quickly. Please wait a moment and try again.' };
  } else if (error instanceof InternalServerError) {
    return { isInternal: true, message: 'OpenAI service is currently unavailable. Please try again later.' };
  } else if (error instanceof APIConnectionError) {
    return { isInternal: true, message: 'Unable to connect to OpenAI service. Please check your API key and internet connection.' };
  } else {
    return { isInternal: false, message: error?.message || 'An unexpected error occurred. Please try again.' };
  }
}

/**
 * Generates a professional Arabic property description using OpenAI GPT-5.
 * @param {Object} property - Property object with details.
 * @param {string} tone - Description tone: 'professional', 'luxury', 'casual', 'marketing'.
 * @returns {Promise<Object>} Generated descriptions in multiple formats.
 */
export async function generateArabicDescription(property, tone = 'professional') {
  try {
    const propertyDetails = `
عنوان العقار: ${property?.title || 'غير محدد'}
نوع العقار: ${property?.type || 'غير محدد'}
الغرض: ${property?.purpose || 'غير محدد'}
عدد غرف النوم: ${property?.bedrooms || 'غير محدد'}
عدد الحمامات: ${property?.bathrooms || 'غير محدد'}
المساحة: ${property?.area || 'غير محدد'} متر مربع
الطابق: ${property?.floor || 'غير محدد'}
التأثيث: ${property?.furnishing || 'غير محدد'}
سنة البناء: ${property?.yearBuilt || 'غير محدد'}
السعر: ${property?.price || 'غير محدد'} ${property?.currency || 'JOD'}
الموقع: ${property?.city || ''} ${property?.district || ''} ${property?.address || ''}
الحالة: ${property?.status || 'متاح'}
المميزات: ${property?.features?.join('، ') || 'لا توجد'}
الوصف الحالي: ${property?.description || 'لا يوجد'}
    `?.trim();

    const toneInstructions = {
      professional: 'استخدم لغة مهنية ورسمية مناسبة للعقارات التجارية والسكنية',
      luxury: 'استخدم لغة فاخرة وراقية تبرز الجودة العالية والتفرد',
      casual: 'استخدم لغة ودية وبسيطة سهلة الفهم',
      marketing: 'استخدم لغة تسويقية جذابة ومقنعة تحفز على الشراء أو الإيجار'
    };

    const response = await openai?.chat?.completions?.create({
      model: 'gpt-5',
      messages: [
        { 
          role: 'system', 
          content: `أنت خبير في كتابة الأوصاف العقارية باللغة العربية. ${toneInstructions?.[tone] || toneInstructions?.professional}. اكتب أوصافاً احترافية وجذابة تسلط الضوء على مميزات العقار وتجذب العملاء المحتملين. استخدم اللغة العربية الفصحى الحديثة.` 
        },
        { 
          role: 'user', 
          content: `اكتب وصفاً احترافياً لهذا العقار باللغة العربية:\n\n${propertyDetails}\n\nقدم الوصف بثلاثة أطوال مختلفة: قصير (2-3 جمل)، متوسط (فقرة واحدة)، وطويل (فقرتان أو أكثر).` 
        },
      ],
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'arabic_property_description',
          schema: {
            type: 'object',
            properties: {
              shortDescription: { type: 'string' },
              mediumDescription: { type: 'string' },
              longDescription: { type: 'string' },
              highlights: { type: 'array', items: { type: 'string' } },
              seoKeywords: { type: 'array', items: { type: 'string' } },
              marketingTitle: { type: 'string' }
            },
            required: ['shortDescription', 'mediumDescription', 'longDescription'],
            additionalProperties: false
          }
        }
      },
      reasoning_effort: 'medium',
      verbosity: 'high'
    });

    return JSON.parse(response?.choices?.[0]?.message?.content);
  } catch (error) {
    const errorInfo = getErrorMessage(error);
    if (errorInfo?.isInternal) {
      console.log(errorInfo?.message);
    } else {
      console.error('Error generating Arabic description:', error);
    }
    throw new Error(errorInfo.message);
  }
}

/**
 * Translates property description to Arabic.
 * @param {string} englishDescription - English description to translate.
 * @param {Object} propertyContext - Additional property context.
 * @returns {Promise<string>} Translated Arabic description.
 */
export async function translateToArabic(englishDescription, propertyContext = {}) {
  try {
    const response = await openai?.chat?.completions?.create({
      model: 'gpt-5-mini',
      messages: [
        { 
          role: 'system', 
          content: 'أنت مترجم محترف متخصص في ترجمة الأوصاف العقارية من الإنجليزية إلى العربية. حافظ على الأسلوب المهني والجاذبية التسويقية.' 
        },
        { 
          role: 'user', 
          content: `ترجم هذا الوصف العقاري إلى اللغة العربية بشكل احترافي:\n\n${englishDescription}\n\nمعلومات إضافية: ${JSON.stringify(propertyContext)}` 
        },
      ],
      reasoning_effort: 'minimal',
      verbosity: 'medium',
      max_completion_tokens: 1000
    });

    return response?.choices?.[0]?.message?.content;
  } catch (error) {
    const errorInfo = getErrorMessage(error);
    if (errorInfo?.isInternal) {
      console.log(errorInfo?.message);
    } else {
      console.error('Error translating to Arabic:', error);
    }
    throw new Error(errorInfo.message);
  }
}

/**
 * Enhances existing Arabic description.
 * @param {string} currentDescription - Current Arabic description.
 * @param {Object} property - Property details.
 * @returns {Promise<string>} Enhanced description.
 */
export async function enhanceArabicDescription(currentDescription, property) {
  try {
    const response = await openai?.chat?.completions?.create({
      model: 'gpt-5-mini',
      messages: [
        { 
          role: 'system', 
          content: 'أنت خبير في تحسين الأوصاف العقارية. قم بتحسين الوصف الحالي مع الحفاظ على المعلومات الأساسية وإضافة تفاصيل جذابة.' 
        },
        { 
          role: 'user', 
          content: `حسّن هذا الوصف العقاري:\n\n${currentDescription}\n\nمعلومات العقار: ${JSON.stringify(property)}` 
        },
      ],
      reasoning_effort: 'minimal',
      verbosity: 'medium',
      max_completion_tokens: 800
    });

    return response?.choices?.[0]?.message?.content;
  } catch (error) {
    const errorInfo = getErrorMessage(error);
    if (errorInfo?.isInternal) {
      console.log(errorInfo?.message);
    } else {
      console.error('Error enhancing Arabic description:', error);
    }
    throw new Error(errorInfo.message);
  }
}
